import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';

const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

function FinancialReportContent() {
  const { t } = useTranslation();
  const { T, S } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  const today = new Date().toISOString().slice(0, 10);
  const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyAgo);
  const [dateTo, setDateTo] = useState(today);

  useEffect(() => {
    api.getManagerBranches().then(setBranches).catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    api.getManagerFinancialReport(dateFrom, dateTo, selectedBranchId || null)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, selectedBranchId]);

  const chartTooltipStyle = { backgroundColor: T.bg, border: `1px solid ${T.border}`, color: T.white };

  const kpiCard = (label, value, sub) => (
    <div style={{ ...S.card, textAlign: 'center', padding: '1.2rem' }}>
      <p style={{ fontSize: '0.75rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.3rem' }}>{label}</p>
      <p style={{ fontSize: '1.6rem', fontWeight: 700, color: T.white, fontFamily: "'DM Sans', sans-serif" }}>{value}</p>
      {sub && <p style={{ fontSize: '0.72rem', color: T.muted, marginTop: '0.2rem' }}>{sub}</p>}
    </div>
  );

  return (
    <AdminLayout role="manager">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={S.pageTitle}>{t('admin.financialReport')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange, marginBottom: '1.5rem' }} />

        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ ...S.input, width: '160px', fontSize: '0.82rem' }} />
          <span style={{ color: T.muted }}>-</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ ...S.input, width: '160px', fontSize: '0.82rem' }} />
          {branches.length > 1 && (
            <select value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}
              style={{ ...S.select, width: '180px', fontSize: '0.82rem' }}>
              <option value="">{t('admin.allBranches')}</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button onClick={() => api.downloadManagerFinancialReport(selectedBranchId || null)}
            style={{ ...S.btnOutline, padding: '0.4rem 0.8rem', fontSize: '0.75rem', height: '36px' }}>
            {t('admin.exportCSV')}
          </button>
        </div>
      </div>

      {loading ? <Skeleton count={6} height="100px" />
        : !data ? <p style={{ color: T.muted }}>{t('admin.reportFailed')}</p>
          : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                {kpiCard(t('report.totalRevenue'), `${data.total_revenue.toLocaleString()} EGP`)}
                {kpiCard(t('report.totalBookings'), data.total_bookings)}
                {kpiCard(t('report.avgBookingValue'), `${data.average_booking_value} EGP`)}
                {kpiCard(t('report.cancellationRate'), `${data.cancellation_rate}%`)}
                {kpiCard(t('report.noShowRate'), `${data.no_show_rate}%`)}
                {kpiCard(t('report.productRevenue'), `${data.product_revenue.toLocaleString()} EGP`, `${data.product_orders_count} ${t('report.orders')}`)}
              </div>

              {/* Revenue Over Time */}
              <div style={{ ...S.card, marginBottom: '1.5rem' }}>
                <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.orange, marginBottom: '1rem' }}>
                  {t('report.revenueOverTime')}
                </h2>
                <div style={{ height: '280px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.revenue_by_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                      <XAxis dataKey="date" stroke={T.muted} fontSize={10} />
                      <YAxis stroke={T.muted} fontSize={10} />
                      <Tooltip contentStyle={chartTooltipStyle} />
                      <Area type="monotone" dataKey="revenue" stroke={T.orange} fill={T.orange} fillOpacity={0.15} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                {/* Revenue by Branch */}
                {data.revenue_by_branch && data.revenue_by_branch.length > 0 && (
                  <div style={S.card}>
                    <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.orange, marginBottom: '1rem' }}>
                      {t('report.revenueByBranch')}
                    </h2>
                    <div style={{ height: '240px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.revenue_by_branch} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                          <XAxis type="number" stroke={T.muted} fontSize={10} />
                          <YAxis dataKey="branch_name" type="category" stroke={T.muted} fontSize={10} width={100} />
                          <Tooltip contentStyle={chartTooltipStyle} />
                          <Bar dataKey="revenue" fill="#10b981" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Revenue by Service */}
                {data.revenue_by_service && data.revenue_by_service.length > 0 && (
                  <div style={S.card}>
                    <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.orange, marginBottom: '1rem' }}>
                      {t('report.revenueByService')}
                    </h2>
                    <div style={{ height: '260px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={data.revenue_by_service} dataKey="revenue" nameKey="service_name" cx="50%" cy="50%"
                            outerRadius={90} label={({ service_name, percent }) => `${service_name} ${(percent * 100).toFixed(0)}%`}
                            labelLine={false} fontSize={10}>
                            {data.revenue_by_service.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={chartTooltipStyle} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Top Artists */}
                {data.revenue_by_artist && data.revenue_by_artist.length > 0 && (
                  <div style={S.card}>
                    <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.orange, marginBottom: '1rem' }}>
                      {t('report.topArtists')}
                    </h2>
                    <div style={{ height: '240px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.revenue_by_artist.slice(0, 8)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                          <XAxis type="number" stroke={T.muted} fontSize={10} />
                          <YAxis dataKey="artist_name" type="category" stroke={T.muted} fontSize={10} width={100} />
                          <Tooltip contentStyle={chartTooltipStyle} />
                          <Bar dataKey="revenue" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>

              {/* Revenue by Month */}
              {data.revenue_by_month && data.revenue_by_month.length > 0 && (
                <div style={{ ...S.card, marginBottom: '1.5rem' }}>
                  <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.orange, marginBottom: '1rem' }}>
                    {t('report.revenueByMonth')}
                  </h2>
                  <div style={{ height: '240px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.revenue_by_month}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                        <XAxis dataKey="month" stroke={T.muted} fontSize={10} />
                        <YAxis stroke={T.muted} fontSize={10} />
                        <Tooltip contentStyle={chartTooltipStyle} />
                        <Bar dataKey="revenue" fill={T.orange} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Top Customers */}
              {data.top_customers && data.top_customers.length > 0 && (
                <div style={S.card}>
                  <h2 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.orange, marginBottom: '1rem' }}>
                    {t('report.topCustomers')}
                  </h2>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem', color: T.muted }}>#</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', color: T.muted }}>{t('report.customerName')}</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', color: T.muted }}>{t('report.totalSpent')}</th>
                          <th style={{ textAlign: 'right', padding: '0.5rem', color: T.muted }}>{t('report.bookingCount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_customers.map((c, i) => (
                          <tr key={c.user_id} style={{ borderBottom: `1px solid ${T.border}` }}>
                            <td style={{ padding: '0.5rem', color: T.muted }}>{i + 1}</td>
                            <td style={{ padding: '0.5rem', color: T.white }}>{c.name}</td>
                            <td style={{ padding: '0.5rem', textAlign: 'right', color: T.white }}>{c.total_spent.toLocaleString()} EGP</td>
                            <td style={{ padding: '0.5rem', textAlign: 'right', color: T.muted }}>{c.booking_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
    </AdminLayout>
  );
}

export default function FinancialReport() {
  return <ProtectedRoute requireSalonManager><FinancialReportContent /></ProtectedRoute>;
}
